import { Controller } from "@hotwired/stimulus"

// Generic copy / share for any canonical URL (issues #497 + #602).
//
// Base class — not registered directly. `referral-share` (referral link) and
// `profile-share` (profile button, #602) subclass it so the share/copy logic
// lives in one place. Subclasses inherit the `targets`/`values` below; the
// data-attribute prefix follows the registered identifier, so callers pass the
// URL with `data-<identifier>-url-value`.
//
// `copy` uses the async Clipboard API when available (secure context only) and
// falls back to a hidden-input + execCommand selection for non-secure contexts
// (e.g. the http LAN host used for mobile testing), so the button never silently
// no-ops. `share` prefers the native Web Share sheet on mobile and falls back to
// copy. All user-facing feedback is i18n, passed in as values from the view.
export default class extends Controller {
  static targets = ["input", "status"]
  static values = {
    url: String,
    copied: String,
    copyError: String,
    shareTitle: String,
    shareText: String
  }

  select() {
    if (this.hasInputTarget) this.inputTarget.select()
  }

  async copy() {
    const ok = await this.#writeToClipboard(this.urlValue)
    this.#flash(ok ? this.copiedValue : this.copyErrorValue)
  }

  async share() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: this.hasShareTitleValue ? this.shareTitleValue : document.title,
          text: this.hasShareTextValue ? this.shareTextValue : "",
          url: this.urlValue
        })
        return
      } catch (err) {
        if (err.name === "AbortError") return
        // Fall through to copy on any other failure.
      }
    }

    this.copy()
  }

  async #writeToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch {
        // Permission denied / not allowed — try the legacy path.
      }
    }
    return this.#legacyCopy(text)
  }

  // execCommand("copy") works in non-secure contexts where the async Clipboard
  // API is unavailable. Selects either the visible readonly input or a throwaway
  // textarea.
  #legacyCopy(text) {
    const source = this.hasInputTarget ? this.inputTarget : this.#scratchInput(text)
    try {
      source.focus()
      source.select()
      source.setSelectionRange(0, text.length)
      const ok = document.execCommand("copy")
      if (!this.hasInputTarget) source.remove()
      return ok
    } catch {
      if (!this.hasInputTarget) source.remove()
      return false
    }
  }

  #scratchInput(text) {
    const el = document.createElement("textarea")
    el.value = text
    el.setAttribute("readonly", "")
    el.style.position = "absolute"
    el.style.left = "-9999px"
    document.body.appendChild(el)
    return el
  }

  #flash(message) {
    if (!this.hasStatusTarget || !message) return
    this.statusTarget.textContent = message
    this.statusTarget.hidden = false
    clearTimeout(this._timer)
    this._timer = setTimeout(() => {
      this.statusTarget.hidden = true
    }, 2500)
  }
}
